import React from "react";
import "../CSS/ContentPage.css";

const TermsAndConditions = () => {
  return (
    <>
      <title>Terms and Conditions | Service Agreement & Legal Terms - Syncnode</title>
      <meta name="description" content="Syncnode's terms and conditions for web development, Android apps, custom software, automation, and IT services. Service scope, intellectual property rights, payment terms, and client responsibilities." />
      <meta name="keywords" content="terms and conditions, service agreement, legal terms, IT services agreement, software development terms, user agreement, terms of service, Syncnode terms" />
      <link rel="canonical" href="https://www.syncnode.in/termsandconditions" />
      <meta property="og:title" content="Terms and Conditions | Service Agreement & Legal Terms - Syncnode" />
      <meta property="og:description" content="Read Syncnode's terms and conditions governing our IT services, web development, Android apps, intellectual property, and service agreements." />
      <meta property="og:url" content="https://www.syncnode.in/termsandconditions" />
      <meta property="og:type" content="website" />
      <div className="contentpage_wrapper">
        <div className="container">
          <div className="content_banner">
            <h1>
              Terms and <span className="decorated_text">Conditions</span>
            </h1>
            <p className="intro_text">
              Please read these terms carefully before using our services. By
              using Syncnode, you agree to these terms.
            </p>

            <div className="last_updated">
              <p>Last Updaed: October 31, 2025</p>
            </div>
          </div>
          <div className="main_content">
            <h2 className="section_heading">Acceptance of Terms</h2>
            <p className="content_para">
              Welcome to Syncnode! These Terms and Conditions ("Terms") govern
              your use of our digital services including Web Development, Mobile
              Development, Automation, E-commerce Solutions, Digital Marketing,
              and Tech Consulting services. By accessing or using our services,
              you acknowledge that you have read, understood, and agree to be
              bound by these Terms.
            </p>
            <p className="content_para">
              If you do not agree with these Terms, please do not use our
              services. Your continued use of our services constitutes
              acceptance of these Terms and any future modifications.
            </p>

            <h2 className="section_heading">Definitions</h2>
            <ul>
              <li>
                <strong>"Syncnode," "we," "us," or "our"</strong> refers to the
                brand providing digital services as described on our website.
              </li>
              <li>
                <strong>"Services"</strong> refer to all digital services
                offered by Syncnode, including but not limited to Web
                Development, Mobile Development, Automation, E-commerce
                Solutions, Digital Marketing, and Tech Consulting.
              </li>
              <li>
                <strong>"User," "Client," "You," or "Your"</strong> refers to
                any individual or entity accessing or using the Services.
              </li>
              <li>
                <strong>"Platform"</strong> refers to the website syncnode.in
                and any related applications or tools.
              </li>
              <li>
                <strong>"Agreement"</strong> refers to any service-specific
                contract, proposal, or statement of work entered into between
                you and Syncnode.
              </li>
              <li>
                <strong>"Content"</strong> refers to all materials, code,
                designs, documentation, and deliverables provided as part of the
                Services.
              </li>
            </ul>

            <h2 className="section_heading">Use of Services</h2>
            <p className="content_para">
              By using our services, you agree to the following conditions:
            </p>
            <ul>
              <li>
                You may only use the Services for lawful purposes and in
                accordance with these Terms and applicable laws
              </li>
              <li>
                You agree not to engage in any activity that may disrupt,
                interfere with, or compromise the integrity or security of the
                Services
              </li>
              <li>
                You are responsible for maintaining the confidentiality of your
                account information and for all activities that occur under your
                account
              </li>
              <li>
                You agree to provide true, accurate, and complete information
                during registration and project initiation, and to keep such
                information updated
              </li>
              <li>
                You will not use the Services to transmit viruses, malware, or
                any other harmful code
              </li>
              <li>
                You will not attempt to gain unauthorized access to any portion
                of the Services or related systems
              </li>
              <li>
                You will not use the Services to infringe upon the intellectual
                property rights of others
              </li>
            </ul>

            <h2 className="section_heading">Service Scope</h2>
            <p className="content_para">
              Syncnode offers the following categories of services:
            </p>

            <h5 className="section_sub_heading">Web Development</h5>
            <ul>
              <li>
                Custom web applications using React, Next.js, and modern
                frameworks
              </li>
              <li>Progressive Web Apps (PWA)</li>
              <li>E-commerce platforms</li>
              <li>API development and integration</li>
              <li>Content Management Systems (CMS)</li>
            </ul>

            <h5 className="section_sub_heading">Mobile Development</h5>
            <ul>
              <li>iOS and Android native applications</li>
              <li>Cross-platform solutions using React Native</li>
              <li>App store optimization and deployment</li>
              <li>Maintenance and updates</li>
            </ul>

            <h5 className="section_sub_heading">Automation Services</h5>
            <ul>
              <li>Business process automation</li>
              <li>Workflow optimization</li>
              <li>API integrations</li>
              <li>Custom scripts and tools</li>
            </ul>

            <h5 className="section_sub_heading">Digital Marketing</h5>
            <ul>
              <li>SEO and content strategy</li>
              <li>Google Ads and PPC management</li>
              <li>Social media marketing</li>
              <li>Analytics and reporting</li>
            </ul>

            <h5 className="section_sub_heading">E-commerce Solutions</h5>
            <ul>
              <li>Platform setup (Shopify, WooCommerce, custom)</li>
              <li>Payment gateway integration</li>
              <li>Inventory management systems</li>
              <li>Conversion optimization</li>
            </ul>

            <h5 className="section_sub_heading">Tech Consulting</h5>
            <ul>
              <li>Technology assessment and strategy</li>
              <li>Architecture planning</li>
              <li>Code review and audit</li>
              <li>Performance optimization</li>
            </ul>
            <p className="content_para">
              <strong>Note:</strong> Specific service details, deliverables,
              timelines, and pricing will be outlined in individual service
              agreements or proposals.
            </p>

            <h2 className="section_heading">Intellectual Property Rights</h2>
            <p className="content_para">
              Intellectual property ownership varies based on the type of
              service and agreement:
            </p>

            <h5 className="section_sub_heading">Our Intellectual Property</h5>
            <ul>
              <li>
                Syncnode retains all intellectual property rights to our
                proprietary tools, methodologies, templates, and frameworks used
                in delivering services
              </li>
              <li>
                Our brand assets including logos, trademarks, website content,
                and marketing materials remain our exclusive property
              </li>
              <li>
                You may not use Syncnode's intellectual property without prior
                written consent
              </li>
            </ul>

            <h5 className="section_sub_heading">
              Custom Development Projects
            </h5>
            <ul>
              <li>
                Upon full payment, you will own the intellectual property rights
                to custom code and designs specifically created for your project
              </li>
              <li>
                Third-party components, libraries, and frameworks retain their
                original licenses
              </li>
              <li>
                We retain the right to use general knowledge, skills, and
                techniques learned during the project
              </li>
              <li>
                Specific ownership terms will be detailed in individual project
                agreements
              </li>
            </ul>

            <h5 className="section_sub_heading">Your Content</h5>
            <ul>
              <li>
                You retain ownership of all content, data, and materials you
                provide to us
              </li>
              <li>
                You grant us a license to use your content solely for the
                purpose of delivering the agreed services
              </li>
              <li>
                You represent that you have all necessary rights to the content
                you provide
              </li>
            </ul>

            <h2 className="section_heading">Data Protection and Privacy</h2>
            <p className="content_para">
              We are committed to protecting your privacy and personal data. Our
              Privacy Policy, which is incorporated into these Terms by
              reference, explains how we collect, use, store, and disclose your
              information.
            </p>
            <p className="content_para">By using the Services, you:</p>
            <ul>
              <li>
                Consent to the collection, use, and disclosure of your
                information as described in our Privacy Policy
              </li>
              <li>
                Acknowledge that we may process your data to provide and improve
                our services
              </li>
              <li>
                Understand that we implement appropriate security measures to
                protect your data
              </li>
              <li>
                Agree to comply with applicable data protection laws when
                providing us with personal information of third parties
              </li>
            </ul>

            <h2 className="section_heading">Payment Terms and Fees</h2>
            <p className="content_para">
              Payment terms are established as follows:
            </p>

            <h5 className="section_sub_heading">Pricing and Invoicing</h5>
            <ul>
              <li>
                All fees will be specified in your service agreement, proposal,
                or on our website
              </li>
              <li>
                Prices are quoted in Indian Rupees (INR) or US Dollars (USD)
                unless otherwise specified
              </li>
              <li>
                All prices are exclusive of applicable taxes unless stated
                otherwise
              </li>
              <li>
                We reserve the right to adjust pricing with 30 days notice for
                ongoing services
              </li>
            </ul>

            <h5 className="section_sub_heading">Payment Methods and Terms</h5>
            <ul>
              <li>
                We accept payments via credit/debit cards, bank transfers, UPI and other specified methods
              </li>
              <li>
                Subscription services are billed monthly or annually in advance
              </li>
              <li>
                Custom development projects typically follow milestone-based
                payments as outlined in the agreement
              </li>
              <li>
                Initial deposits or advance payments may be required to commence
                work
              </li>
              <li>
                Payment terms (net 7, net 15, net 30 days) will be specified on
                invoices
              </li>
            </ul>

            <h5 className="section_sub_heading">Late Payments and Suspension</h5>
            <ul>
              <li>
                Late payment may result in suspension or termination of services
              </li>
              <li>
                Interest may be charged on overdue amounts at a rate of 1.5% per
                month or the maximum allowed by law
              </li>
              <li>
                We reserve the right to engage collection agencies for
                significantly overdue accounts
              </li>
              <li>
                You remain responsible for all fees incurred prior to suspension
                or termination
              </li>
            </ul>

            <h5 className="section_sub_heading">Refunds</h5>
            <p className="content_para">
              Refund eligibility is governed by our Refund and Cancellation
              Policy. Please review that policy for detailed information on
              refund conditions, timeframes, and processes.
            </p>

            <h2 className="section_heading">Service Level and Support</h2>

            <h5 className="section_sub_heading">Service Availability</h5>
            <ul>
              <li>
                We strive to maintain high availability of our services but do
                not guarantee uninterrupted access
              </li>
              <li>
                Scheduled maintenance will be communicated in advance when
                possible
              </li>
              <li>
                We are not liable for downtime caused by factors beyond our
                reasonable control
              </li>
            </ul>

            <h5 className="section_sub_heading">Support Services</h5>
            <ul>
              <li>
                Standard support is provided during business hours: 9:00 AM -
                6:00 PM IST, Monday to Saturday
              </li>
              <li>
                Support channels include email, phone, and our support portal
              </li>
              <li>
                Response times vary based on the nature and urgency of the issue
              </li>
              <li>
                Extended support hours or dedicated support can be arranged
                through separate agreements
              </li>
            </ul>

            <h5 className="section_sub_heading">Project Delivery</h5>
            <ul>
              <li>
                Project timelines will be specified in individual service
                agreements
              </li>
              <li>
                Delivery dates are estimates and may be adjusted due to scope
                changes or unforeseen circumstances
              </li>
              <li>
                Client cooperation and timely provision of required materials
                are essential for meeting timelines
              </li>
            </ul>

            <h2 className="section_heading">Client Responsibilities</h2>
            <p className="content_para">
              To ensure successful project delivery, you agree to:
            </p>
            <ul>
              <li>
                Provide timely and accurate information, content, and materials
                required for the project
              </li>
              <li>
                Respond to requests for feedback and approvals within agreed
                timeframes
              </li>
              <li>Designate an authorized representative for project decisions</li>
              <li>
                Ensure you have necessary rights and permissions for all
                materials provided
              </li>
              <li>
                Maintain confidentiality of access credentials and account
                information
              </li>
              <li>
                Backup your data regularly; we are not responsible for data loss
              </li>
              <li>
                Test deliverables and report issues within specified acceptance
                periods
              </li>
            </ul>

            <h2 className="section_heading">Changes and Modifications</h2>

            <h5 className="section_sub_heading">Scope Changes</h5>
            <ul>
              <li>
                Changes to project scope, features, or requirements must be
                requested in writing
              </li>
              <li>
                Scope changes may result in timeline extensions and additional
                fees
              </li>
              <li>
                We will provide revised estimates for approval before
                implementing changes
              </li>
            </ul>

            <h5 className="section_sub_heading">Service Modifications</h5>
            <ul>
              <li>
                We reserve the right to modify, suspend, or discontinue any
                aspect of our services with reasonable notice
              </li>
              <li>
                We will make reasonable efforts to minimize disruption from
                service modifications
              </li>
            </ul>

            <h2 className="section_heading">Confidentiality</h2>
            <p className="content_para">
              Both parties agree to maintain the confidentiality of sensitive
              information:
            </p>
            <ul>
              <li>
                We will protect your confidential business information, trade
                secrets, and proprietary data
              </li>
              <li>
                You agree to keep confidential any proprietary methodologies,
                tools, or processes we share
              </li>
              <li>
                Confidentiality obligations survive termination of the agreement
              </li>
              <li>
                Exceptions include information that is publicly available or
                required to be disclosed by law
              </li>
            </ul>

            <h2 className="section_heading">Warranties and Disclaimers</h2>

            <h5 className="section_sub_heading">Our Warranties</h5>
            <ul>
              <li>
                We warrant that services will be performed in a professional and
                workmanlike manner
              </li>
              <li>
                We warrant that we have the right to provide the services and
                deliverables
              </li>
              <li>
                Custom code will be free from material defects for a warranty
                period specified in your agreement
              </li>
            </ul>

            <h5 className="section_sub_heading">Disclaimers</h5>
            <ul>
              <li>
                Services are provided "as is" except as explicitly warranted in
                your service agreement
              </li>
              <li>
                We do not warrant that services will meet all your requirements
                or be error-free
              </li>
              <li>
                We are not responsible for results or outcomes from your use of
                our services
              </li>
              <li>
                We disclaim warranties regarding third-party products, services,
                or platforms integrated into your project
              </li>
            </ul>

            <h2 className="section_heading">Limitation of Liability</h2>
            <p className="content_para">
              To the maximum extent permitted by law:
            </p>
            <ul>
              <li>
                Syncnode shall not be liable for any indirect, incidental,
                consequential, special, or punitive damages
              </li>
              <li>
                This includes but is not limited to loss of profits, data,
                revenue, or business opportunities
              </li>
              <li>
                Our total liability for any claims arising from or related to
                the services shall not exceed the fees you paid to us in the six
                (6) months preceding the claim
              </li>
              <li>
                These limitations apply regardless of the legal theory
                (contract, tort, negligence, etc.)
              </li>
              <li>
                Some jurisdictions do not allow certain limitations, so these
                may not apply to you
              </li>
            </ul>

            <h2 className="section_heading">Force Majeure</h2>
            <p className="content_para">
              We shall not be liable for any failure to perform due to
              circumstances beyond our reasonable control, including natural
              disasters, war, terrorism, riots, embargoes, acts of civil or
              military authorities, fire, floods, accidents, pandemics, network
              infrastructure failures, strikes, or shortages of transportation,
              facilities, fuel, energy, labor, or materials.
            </p>

            <h2 className="section_heading">Indemnification</h2>
            <p className="content_para">
              You agree to indemnify, defend, and hold Syncnode harmless from
              and against any claims, liabilities, damages, losses, costs, and
              expenses (including reasonable attorney's fees) arising out of or
              related to:
            </p>
            <ul>
              <li>Your use of the services in violation of these Terms</li>
              <li>Your violation of any applicable laws or regulations</li>
              <li>
                Your infringement of any third-party intellectual property or
                other rights
              </li>
              <li>Content or materials you provide that cause legal claims</li>
              <li>Your negligence or willful misconduct</li>
            </ul>

            <h2 className="section_heading">Termination</h2>

            <h5 className="section_sub_heading">Termination by You</h5>
            <ul>
              <li>
                You may terminate subscription services by providing written
                notice as specified in your service agreement
              </li>
              <li>
                Project-based services can be cancelled subject to our Refund
                and Cancellation Policy
              </li>
              <li>
                You remain responsible for all fees incurred prior to
                termination
              </li>
            </ul>

            <h5 className="section_sub_heading">Termination by Us</h5>
            <ul>
              <li>
                We may suspend or terminate your access for violation of these
                Terms
              </li>
              <li>
                We may terminate for non-payment after appropriate notice and
                opportunity to cure
              </li>
              <li>
                We may terminate immediately for illegal activity or severe
                violations
              </li>
              <li>We may terminate with 30 days notice for business reasons</li>
            </ul>

            <h5 className="section_sub_heading">Effect of Termination</h5>
            <ul>
              <li>
                Upon termination, your right to access and use the services will
                cease
              </li>
              <li>Outstanding payments become immediately due</li>
              <li>
                We will provide completed deliverables upon receipt of final
                payment
              </li>
              <li>You must cease use of our proprietary materials and tools</li>
              <li>
                Provisions that by their nature should survive will survive
                termination
              </li>
            </ul>

            <h2 className="section_heading">Dispute Resolution</h2>
            <p className="content_para">
              In the event of any dispute arising from these Terms or our
              services:
            </p>
            <ul>
              <li>
                <strong>Negotiation:</strong> Both parties will first attempt to
                resolve disputes through good faith negotiation
              </li>
              <li>
                <strong>Mediation:</strong> If negotiation fails, both parties
                agree to attempt mediation before pursuing litigation
              </li>
              <li>
                <strong>Arbitration:</strong> Disputes that cannot be resolved
                through mediation may be submitted to binding arbitration in
                accordance with applicable arbitration rules
              </li>
              <li>
                <strong>Litigation:</strong> If arbitration is not applicable or
                agreed upon, disputes will be resolved in courts with
                appropriate jurisdiction
              </li>
            </ul>

            <h2 className="section_heading">Governing Law and Jurisdiction</h2>
            <p className="content_para">
              These Terms shall be governed by and construed in accordance with
              the laws of India, without regard to its conflict of law
              provisions. Any legal action or proceeding arising from these
              Terms shall be brought exclusively in the courts of appropriate
              jurisdiction in India.
            </p>

            <h2 className="section_heading">General Provisions</h2>

            <h5 className="section_sub_heading">Entire Agreement</h5>
            <p className="content_para">
              These Terms, together with any service-specific agreements,
              proposals, and our Privacy Policy and Refund Policy, constitute
              the entire agreement between you and Syncnode regarding the
              subject matter herein and supersede all prior agreements and
              understandings.
            </p>

            <h5 className="section_sub_heading">Amendments</h5>
            <p className="content_para">
              We reserve the right to modify these Terms at any time. Material
              changes will be communicated via email or prominent notice on our
              website. Your continued use of the services after such
              modifications constitutes acceptance of the updated Terms. Changes
              to individual service agreements require mutual written consent.
            </p>

            <h5 className="section_sub_heading">Severability</h5>
            <p className="content_para">
              If any provision of these Terms is found to be unenforceable or
              invalid, that provision will be limited or eliminated to the
              minimum extent necessary, and the remaining provisions will remain
              in full force and effect.
            </p>

            <h5 className="section_sub_heading">Waiver</h5>
            <p className="content_para">
              No waiver of any term or condition shall be deemed a further or
              continuing waiver of such term or any other term. Our failure to
              assert any right or provision under these Terms shall not
              constitute a waiver of such right or provision.
            </p>

            <h5 className="section_sub_heading">Assignment</h5>
            <p className="content_para">
              You may not assign or transfer these Terms or your rights
              hereunder without our prior written consent. We may assign these
              Terms without restriction. Any attempted assignment in violation
              of this provision is void.
            </p>

            <h5 className="section_sub_heading">Third-Party Services</h5>
            <p className="content_para">
              Our services may integrate with or include third-party services,
              products, or content. We are not responsible for third-party
              services and their use is subject to their own terms and
              conditions.
            </p>

            <h5 className="section_sub_heading">Notice</h5>
            <p className="content_para">
              Notices to you may be made via email, regular mail, or by posting
              on our website. Notices to us should be sent via email to the
              address provided in the Contact section below.
            </p>

            <h2 className="section_heading">Contact Information</h2>
            <p className="content_para">
              If you have any questions, concerns, or notices regarding these
              Terms, please contact us:
            </p>
            <div className="highlight_box">
              <ul>
                <li>
                  <strong>Email:</strong> info@syncnode.in
                </li>
                <li>
                  <strong>Phone:</strong> +91 7876666787
                </li>
                <li>
                  <strong>Business Hours:</strong> 9:00 AM - 6:00 PM IST, Monday
                  to Friday
                </li>
              </ul>
            </div>

            <div className="highlight_box">
              <p className="content_para">
                <strong>Acknowledgment:</strong> By using the Services, you
                acknowledge that you have read, understood, and agree to be
                bound by these Terms and Conditions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsAndConditions;
